/*
 * KORVN Guides — topic filter (Guides index) and the compact homepage carousel.
 * Both are progressive enhancement: without JS the index shows every guide and
 * the carousel is a native, swipeable scroll-snap row.
 */
(() => {
  if (window.__korvnGuides) return;
  window.__korvnGuides = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Auto-advance only where there are arrows to match (tablet/desktop); mobile is swipe-only.
  const wide = window.matchMedia('(min-width: 750px)');

  /* ---------------- topic filter ---------------- */
  function initFilter(root) {
    const bar = root.querySelector('[data-korvn-guides-filter]');
    const grid = root.querySelector('[data-korvn-guides-grid]');
    if (!bar || !grid) return;
    const chips = [...bar.querySelectorAll('[data-topic]')];
    const items = [...grid.children];
    const empty = root.querySelector('[data-korvn-guides-empty]');
    const status = root.querySelector('[data-korvn-guides-status]');

    // Hide chips for topics with no guides, and show counts.
    chips.forEach((chip) => {
      const t = chip.dataset.topic;
      const n = t ? items.filter((li) => hasTopic(li, t)).length : items.length;
      if (t && n === 0) chip.remove();
      else chip.insertAdjacentHTML('beforeend', ` <span class="korvn-chip__count">${n}</span>`);
    });
    bar.hidden = false;

    function hasTopic(li, topic) {
      const card = li.querySelector('[data-topics]');
      return !!card && card.dataset.topics.split(' ').includes(topic);
    }

    function apply(topic, updateHash) {
      let shown = 0;
      items.forEach((li) => {
        const match = !topic || hasTopic(li, topic);
        li.hidden = !match;
        if (match) shown += 1;
      });
      bar.querySelectorAll('[data-topic]').forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.topic === topic)));
      if (empty) empty.hidden = shown > 0;
      if (status) {
        const label = bar.querySelector(`[data-topic="${topic}"]`);
        status.textContent = `${shown} guide${shown === 1 ? '' : 's'}${topic && label ? ` in ${label.firstChild.textContent.trim()}` : ''}`;
      }
      if (updateHash) {
        const url = new URL(window.location.href);
        url.hash = topic ? `topic-${topic}` : '';
        history.replaceState(null, '', url);
      }
    }

    bar.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-topic]');
      if (chip) apply(chip.dataset.topic, true);
    });

    const fromHash = () => {
      const m = window.location.hash.match(/^#topic-([a-z0-9-]+)$/);
      if (m && bar.querySelector(`[data-topic="${m[1]}"]`)) {
        apply(m[1], false);
        return true;
      }
      return false;
    };
    if (fromHash()) root.querySelector('#korvn-guides-all')?.scrollIntoView({ block: 'start' });
    window.addEventListener('hashchange', fromHash);
  }

  /* ---------------- carousel ---------------- */
  function initCarousel(root) {
    const track = root.querySelector('[data-track]');
    if (!track) return;
    const prev = root.querySelector('[data-action="prev"]');
    const next = root.querySelector('[data-action="next"]');
    const toggle = root.querySelector('[data-action="toggle"]');
    // Auto-advance only when the section enables it (data-interval present).
    const autoplay = 'interval' in root.dataset;
    const interval = Math.max(5000, Number(root.dataset.interval) || 8000);

    const step = () => {
      const slide = track.querySelector('.korvn-gcarousel__slide');
      if (!slide) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    };
    const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    const atStart = () => track.scrollLeft <= 4;
    const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

    const syncButtons = () => {
      const scrollable = track.scrollWidth > track.clientWidth + 4;
      if (prev) prev.disabled = !scrollable || atStart();
      if (next) next.disabled = !scrollable || atEnd();
      root.classList.toggle('is-static', !scrollable);
    };
    const go = (dir) => track.scrollBy({ left: dir * step(), behavior: behavior() });

    prev?.addEventListener('click', () => go(-1));
    next?.addEventListener('click', () => go(1));
    track.addEventListener('scroll', () => requestAnimationFrame(syncButtons), { passive: true });
    window.addEventListener('resize', syncButtons);
    syncButtons();

    // Auto-advance: slow, one card at a time, looping back to the start.
    let timer = null;
    let userPaused = false;
    let hovering = false;
    let focused = false;
    let touching = false;

    const canRun = () =>
      autoplay && wide.matches && !reduceMotion.matches && !userPaused && !hovering && !focused && !touching &&
      document.visibilityState === 'visible' && track.scrollWidth > track.clientWidth + 4;

    // Only advance while at least 40% of the carousel is on screen. (Checked
    // per tick rather than with IntersectionObserver, which reports 0 inside
    // some Hyper section wrappers.)
    const onScreen = () => {
      const r = root.getBoundingClientRect();
      const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
      return visible >= r.height * 0.4;
    };
    const tick = () => {
      if (!canRun() || !onScreen()) return;
      if (atEnd()) track.scrollTo({ left: 0, behavior: behavior() });
      else go(1);
    };
    const schedule = () => {
      clearInterval(timer);
      timer = canRun() ? setInterval(tick, interval) : null;
    };

    const setToggle = () => {
      if (!toggle) return;
      toggle.hidden = reduceMotion.matches;
      toggle.setAttribute('aria-pressed', String(userPaused));
      toggle.classList.toggle('is-paused', userPaused);
      toggle.querySelector('[data-label]').textContent = userPaused ? 'Resume automatic scrolling' : 'Pause automatic scrolling';
    };
    toggle?.addEventListener('click', () => { userPaused = !userPaused; setToggle(); schedule(); });

    root.addEventListener('mouseenter', () => { hovering = true; schedule(); });
    root.addEventListener('mouseleave', () => { hovering = false; schedule(); });
    root.addEventListener('focusin', () => { focused = true; schedule(); });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { focused = false; schedule(); } });
    track.addEventListener('touchstart', () => { touching = true; schedule(); }, { passive: true });
    track.addEventListener('touchend', () => { setTimeout(() => { touching = false; schedule(); }, interval); }, { passive: true });
    document.addEventListener('visibilitychange', schedule);
    reduceMotion.addEventListener?.('change', () => { setToggle(); schedule(); });
    wide.addEventListener?.('change', schedule);

    schedule();
    setToggle();
  }

  const init = () => {
    document.querySelectorAll('[data-korvn-guides]').forEach(initFilter);
    document.querySelectorAll('[data-korvn-gcarousel]').forEach(initCarousel);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
